import { FC, useCallback, useEffect, useRef, useState } from 'react'
import styles from './MagicScroll.module.scss'
import { Waypoint } from 'react-waypoint'
import { Video } from 'components/basic/Video'
import { Img } from 'components/basic/Img'

interface ISlide {
	name: string
	description: string
	thumb: string
	source: string
	caption?: string
	label?: string
	image?: string
	imageMobile?: string
	alt?: string
}

interface MagicScrollProps {
	title?: string
	description?: string
}

const avaliableFrames: string[] = []
const NUM_FRAMES = 61
const SCROLL_RATE = 10
const SLIDE_HEIGHT = 100

const SLIDES: ISlide[] = [
	{
		source: 'https://eightsleep.imgix.net/vid_1.mp4?v=1654084545',
		thumb: '',
		name: 'Climb into bed, already set at your perfect temperature',
		label: '<strong>11:00pm</strong> Bedtime',
		description:
			'The Pod automatically heats or cools before your bedtime so you get into bed comfortably and fall asleep faster. If you sleep with a partner, their side will automatically set to their temperature preferences as well. ',
	},
	{
		source: 'https://eightsleep.imgix.net/vid2_2.mp4?v=1654258254',
		thumb: '',
		name: 'Sleep deeper while the Pod adjusts to your optimal temperature',
		label: '<strong>3:00am</strong> Deep Phase',
		description:
			'With Temperature Autopilot, the Pod adjusts your sleeping temperature to your individual patterns, supporting the flow of each sleep cycle to maximize recovery. Temperature on each side of the bed can range from as low as 55℉ to as high as 110℉.',
	},
	{
		source: 'https://eightsleep.imgix.net/vid_3.mp4?v=1654084545',
		thumb: '',
		name: 'Wake up with a gentle vibration, and more energy',
		label: '<strong>7:00am</strong> Wake Up',
		description: 'Let GentleRise wake you up with a gradual temperature change and a subtle, chest-level vibration on your side of the Pod. No more harsh sounds or disturbing your partner.',
	},
	{
		source: 'https://eightsleep.imgix.net/vid_4.mp4?v=1654084545',
		thumb: '',
		name: 'Track your sleep and health metrics, with no wearable required',
		image: 'https://eightsleep.imgix.net/new_spaced_insights.png?v=1658500491',
		imageMobile: 'https://eightsleep.imgix.net/insight_fixed.png?v=1658763925',
		alt: 'iPhone 12 white',
		description:
			'Monitor your sleep and health metrics, including sleep stages, HRV, heart rate, and respiratory rate. No wearable required. No sacrifice to comfort. Monitoring your health has never been so easy.',
	},
]

const getImages = () => {
	if (avaliableFrames.length === 0) {
		for (let i = 0; i < NUM_FRAMES; i++) {
			fetch(`https://eightsleep.imgix.net/magic-parallax-f_2_${i.toString().padStart(5, '0')}.jpg`).then((r) => {
				if (r.status === 200) {
					r.blob().then((b) => {
						avaliableFrames[i] = URL.createObjectURL(b)
					})
				}
			})
		}
	}
}

const calculateMagicHeight = (i: number, offset?: string) => `calc(${SCROLL_RATE * NUM_FRAMES * 1.2}px + ${(i + 1) * SLIDE_HEIGHT}vh${offset != null ? offset : ''})`

export const MagicScroll: FC<MagicScrollProps> = () => {
	const [activeSlide, setActiveSlide] = useState(-1)
	const [scrollAnimationFadeOut, setScrollAnimationFadeOut] = useState(false)
	const [scrollAnimationTranslateProgress, setScrollAnimationTranslateProgress] = useState(0)
	const [headingFadeOut, setHeadingFadeOut] = useState(false)
	const [scrollImageSrc, setScrollImageSrc] = useState('https://eightsleep.imgix.net/magic-parallax-f_2_00000.jpg?v=1654238000')
	const scrollAnimatedContainerRef = useRef<HTMLDivElement | null>(null)

	const maxTopOffset = typeof window !== 'undefined' ? window.innerHeight : 0
	const minTopOffset = maxTopOffset * ((100 - SLIDE_HEIGHT) / 100)

	const handleScrollUpdate = useCallback(() => {
		if (scrollAnimatedContainerRef.current) {
			const viewportOffset = scrollAnimatedContainerRef.current.getBoundingClientRect()
			setHeadingFadeOut(false)
			if (viewportOffset.top < 0) {
				const frame = Math.round(Math.abs(viewportOffset.top) / SCROLL_RATE)
				if (avaliableFrames[frame]) {
					setScrollImageSrc(avaliableFrames[frame])
					setScrollAnimationTranslateProgress(frame / avaliableFrames.length)

					if (frame >= avaliableFrames.length * 0.2) {
						setHeadingFadeOut(true)
					} else {
						setHeadingFadeOut(false)
					}
				} else {
					setScrollAnimationTranslateProgress(1)
					setHeadingFadeOut(true)
					const waaypoint = document.getElementById(`slide-waypoint-${activeSlide}`)
					const topOffset = waaypoint?.getBoundingClientRect().top
					if (topOffset) {
						const calc = ((topOffset - minTopOffset - 100) / (maxTopOffset - minTopOffset)) * 100
						if (calc < 0 && activeSlide === SLIDES.length - 1) return
						scrollAnimatedContainerRef.current.style.setProperty(`--slideScrollProgress`, `${calc}%`)
					}
				}
			}
			if (viewportOffset.bottom < 0) {
				setScrollImageSrc(avaliableFrames[avaliableFrames.length - 1])
				setScrollAnimationTranslateProgress(1)
				setHeadingFadeOut(true)
				setActiveSlide(-1)
				scrollAnimatedContainerRef.current.style.setProperty(`--slideScrollProgress`, `0%`)
			}
		}
	}, [activeSlide, maxTopOffset, minTopOffset])

	useEffect(() => {
		getImages()
		window.addEventListener('scroll', handleScrollUpdate)
		return () => {
			window.removeEventListener('scroll', handleScrollUpdate)
		}
	}, [handleScrollUpdate])

	return (
		<div className={styles.container}>
			<div
				ref={scrollAnimatedContainerRef}
				className={styles.scroll_animation_container}
				style={{ height: calculateMagicHeight(SLIDES.length) }}
			>
				<div className={`${styles.scroll_animated_image} ${scrollAnimationFadeOut ? styles.scroll_fadeOut : ''}`}>
					{scrollAnimationFadeOut ? (
						<div className={`${styles.navigator}`}>
							{SLIDES.map((it, i) => (
								<div
									key={`slide-indicator-${i}`}
									className={activeSlide === i ? styles.indicator_active : ''}
								></div>
							))}
						</div>
					) : (
						<></>
					)}

					<div
						className={styles.img_container}
						style={{ transform: `translateY(calc(var(--heading-container-height) * ${scrollAnimationTranslateProgress} * -1))` }}
					>
						<div className={styles.scrollable_img}>
							{/* eslint-disable-next-line @next/next/no-img-element */}
							<img
								alt={'Pod 3 scroll frame'}
								src={scrollImageSrc}
							/>
						</div>
						{SLIDES.map((slide, i) => (
							<div
								key={`video-scroll-${i}`}
								className={`${styles.animated_video} ${activeSlide === i && scrollAnimationFadeOut ? styles.video_show : ''} ${slide.image && slide.imageMobile ? styles.no_aspect_ratio : ''}`}
							>
								<Video
									thumb={slide.thumb}
									src={slide.source}
									caption={slide.caption || ''}
									showControls={false}
									className={slide.imageMobile ? styles.hidden : ''}
								/>
								{slide.label ? (
									<div
										className={styles.label}
										dangerouslySetInnerHTML={{ __html: slide.label }}
									></div>
								) : (
									<></>
								)}
								{slide.image && slide.imageMobile ? (
									<Img
										className={styles.picture}
										src={slide.imageMobile}
										alt={slide.alt!}
										sources={[
											{
												src: slide.image,
												mediaQuery: '(min-width: 1024px)',
											},
											{
												src: slide.imageMobile,
												mediaQuery: '(max-width: 1023px)',
											},
										]}
									/>
								) : (
									<></>
								)}
							</div>
						))}
					</div>
					<div className={styles.descriptions}>
						<div>
							{SLIDES.map((slide, i) => (
								<div
									key={`slide-description-${i}`}
									className={`${styles.description} ${activeSlide >= i && scrollAnimationFadeOut ? styles.description_show : ''} ${activeSlide > i ? styles.fadeOut : ''}`}
									style={{ transform: scrollAnimationFadeOut && activeSlide >= i ? `translateY(var(--slideScrollProgress))` : 'translateY(calc(100% + 50px))' }}
								>
									<p>{slide.name}</p>
									<p>{slide.description}</p>
								</div>
							))}
						</div>
					</div>
					<div className={`${styles.heading} ${headingFadeOut ? styles.heading_fadeout : ''}`}>
						<h2>
							It’s not <span className={styles.magic_font}>magic</span>. <br /> But it feels like it.
						</h2>
						<p>Technology designed to fit onto your bed and improve your sleep, without any extra work for you.</p>
					</div>
				</div>
				<div className={styles.waypoint_special}>
					<Waypoint
						onEnter={() => {
							setScrollAnimationFadeOut(false)
						}}
					/>
				</div>
				{SLIDES.map((slide, i) => (
					<div
						key={`slide-waypoint-${i}`}
						id={`slide-waypoint-${i}`}
						className={styles.waypoint}
						style={{ top: calculateMagicHeight(i) }}
					>
						<Waypoint
							onEnter={() => {
								setActiveSlide(i)
								setScrollAnimationFadeOut(true)
							}}
							onLeave={() => {
								if (i === SLIDES.length - 1 && activeSlide === i) {
									// setActiveSlide(i + 1)
								}
								if (i === 0 && activeSlide === i) {
									setActiveSlide(-1)
									setScrollAnimationFadeOut(false)
								}
							}}
						/>
					</div>
				))}
			</div>
		</div>
	)
}
